import { Box, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { CompanyActiveState, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { ICompanyListItem } from "../../../models/company/companyListItem";
import { CompanyListItems, ICompanyListItems } from "../../../models/company/companyListItems";
import * as companyService from "../../../models/services/companyService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { List } from "../../framework/List/List";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ISelectCompanyDialogProps {
    classes?: string;
    records?: ICompanyListItems;
    ignoreCompanyIds?: string[];
    ignoreCompanyGroupId?: string;
    multiSelect?: boolean;
    title?: string;
    onOk: (companies: ICompanyListItem[]) => void;
    onCancel: () => void;
    onGetCompanyListItems?: (pageSize: number, page: number, filter: string, companyGroupIds: string[],
        activityState: number, sortColumn: string, sortOrderIsAsc: boolean,
        ignoreCompanyGroupId: string, ignoreCompanyIds: string[]) => Promise<CompanyListItems>
}

export interface ISelectCompanyDialogState {
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    hasMore: boolean;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    items: ICompanyListItem[];
    selectedId: string;
    checkedIds: string[];
}

export class SelectCompanyDialog extends React.Component<ISelectCompanyDialogProps, ISelectCompanyDialogState> {
    constructor(props) {
        super(props);
        this.state = {
            intialized: !!props.records,
            isLoading: false,
            waitingPreloadedData: !props.records,
            pageSize: props.records ? props.records.pageSize : infiniteListPageSize,
            page: props.records ? props.records.page : 1,
            hasMore: props.records ? props.records.hasMore : true,
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            items: props.records ? props.records.items : [],
            selectedId: null,
            checkedIds: [],
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean) => {
        const props = this.props;
        const state = this.state;
        this.setState({ isLoading: true });
        const onGetCompanyListItems = props.onGetCompanyListItems ?? companyService.getCompanyListItems;
        const result = await AppUtils.callService(() => onGetCompanyListItems(!refreshList ? pageSize : (page * infiniteListPageSize), !refreshList ? page : 1, filter, null, 0, sortColumn, sortOrderIsAsc, props.ignoreCompanyGroupId, props.ignoreCompanyIds),
            false, true, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId: items.selectedId,
            checkedIds: items.checkedIds,
        });
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.records) return;
        this.searchItems(state.pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentDidUpdate(prevProps: ISelectCompanyDialogProps, prevState: ISelectCompanyDialogState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.records) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.records.page,
                hasMore: props.records.hasMore,
                items: props.records.items,
                pageSize: props.records.pageSize,
                sortColumn: props.records.sortColumn,
                sortOrderIsAsc: props.records.sortOrderIsAsc
            });
        } else {
            const prevIgnoreCompanyGroupId = prevProps.ignoreCompanyGroupId ? prevProps.ignoreCompanyGroupId : "";
            const ignoreCompanyGroupId = props.ignoreCompanyGroupId ? props.ignoreCompanyGroupId : "";
            if (ignoreCompanyGroupId === prevIgnoreCompanyGroupId && Base.equalStrArrays(prevProps.ignoreCompanyIds, props.ignoreCompanyIds)) return;
            this.searchItems(state.pageSize, 1, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleCancel = () => {
        this.props.onCancel();
    };

    handleOk = () => {
        const state = this.state;
        const ids = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (ids.length < 1) return;
        const items = state.items.filter(i => ids.indexOf(i.id) > -1);
        if (items.length < 1) return;
        this.props.onOk(items);
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <Dialog
                id="select-company-dialog"
                maxHeight="726px"
                maxWidth="md"
                hide={!state.intialized}
                title={props.title ?? Translations.SelectCompany}
                body={
                    <VerticalContainer
                        before={<>
                            <ActionContainer
                                leftItems={
                                    <ListSearchFilter
                                        searchFilter={state.filter}
                                        onSearchClick={this.changeFilter} />
                                }
                            />
                        </>}
                        main={<>
                            <List
                                multiSelect={props.multiSelect}
                                intialized={state.intialized}
                                isLoading={state.isLoading}
                                hasMore={state.hasMore}
                                height="100%"
                                header={
                                    {
                                        columns: [
                                            { size: 4, column: "code", content: Translations.Code },
                                            { size: 5, column: "name", content: Translations.Name },
                                            { size: 3, column: "activeState", content: Translations.ActiveState }
                                        ]
                                    }
                                }
                                lines={
                                    state.items.map((item) => {
                                        return {
                                            columns: [
                                                {
                                                    size: 4,
                                                    column: "code",
                                                    content:
                                                        <Box display="flex" alignItems="center">
                                                            <AvatarImage
                                                                name={item.name}
                                                                image={item.logo}
                                                                tooltip={item.name}
                                                                marginRight=".5rem"
                                                            />
                                                            <Typography>{item.code}</Typography>
                                                        </Box>
                                                },
                                                { size: 5, column: "name", content: item.name },
                                                { size: 3, column: "activeState", content: item.activeStateStr },
                                            ],
                                            id: item.id,
                                            classes: EnumHelper.isEqual(item.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                        };
                                    })}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                            />
                        </>}
                    />
                }
                buttons={[
                    { title: Translations.Cancel, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleOk },
                ]}
                onClose={this.handleCancel}
            />
        );
    }
}